// Klantenlijst voor wisselaar, zijbalk en platformdashboard. Een platformbeheerder ziet alle klanten; bij klanten
// zonder lidmaatschap neemt hij zichtbaar en tijdelijk toegang (ondersteuningslidmaatschap met reden, vervaldatum en
// audit, ADR 0007). Klantdata blijft alleen bereikbaar via een lidmaatschap.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog, toast } from "@bres/ui";
import { api } from "../lib/api.ts";
import { errorText } from "../lib/errors.tsx";
import { useI18n } from "../lib/i18n.tsx";
import { useIsPlatformAdmin, useSessionState, type Me } from "../lib/session.tsx";
import { usePlatformOverview } from "../screens/platform/data.ts";

/** Standaardduur van platformtoegang. Daarna vervalt hij vanzelf; opnieuw nemen kan altijd. */

export interface ClientEntry {
  id: string;
  name: string;
  isDemo: boolean;
  /** Lidmaatschap van de ingelogde gebruiker, of null als hij (nog) geen toegang heeft. */
  membership: NonNullable<Me["tenants"]>[number] | null;
  /** Vaste kleur per klant, gelijk in wisselaar, zijbalk en dashboard. */
  color: number;
}

export function clientColor(index: number): number {
  return (index % 4) + 1;
}

/** Alle klanten die deze gebruiker ziet: eigen lidmaatschappen, plus voor een platformbeheerder alle overige klanten. */
export function useClientDirectory(): { clients: ClientEntry[]; loading: boolean } {
  const { me } = useSessionState();
  const platformAdmin = useIsPlatformAdmin();
  const overview = usePlatformOverview(platformAdmin);
  const tenants = me.tenants ?? [];
  const byId = new Map(tenants.map((x) => [x.id, x]));
  if (!platformAdmin || !overview.data) {
    return { clients: tenants.map((x, i) => ({ id: x.id, name: x.name, isDemo: x.is_demo, membership: x, color: clientColor(i) })), loading: platformAdmin && overview.isPending };
  }
  const listed = overview.data.tenants.map((x, i) => ({ id: x.id, name: x.name, isDemo: x.isDemo, membership: byId.get(x.id) ?? null, color: clientColor(i) }));
  // Lidmaatschappen bij klanten die (nog) niet in het overzicht staan, vallen niet weg.
  const extra = tenants.filter((x) => !listed.some((l) => l.id === x.id)).map((x, i) => ({ id: x.id, name: x.name, isDemo: x.is_demo, membership: x, color: clientColor(listed.length + i) }));
  return { clients: [...listed, ...extra], loading: false };
}

/** Wissel naar een klant waar de gebruiker lid van is (gewone tenantwissel, de server controleert het lidmaatschap). */
export async function switchToTenant(tenantId: string, qc: ReturnType<typeof useQueryClient>) {
  await api.post("/v1/me/tenant", { tenantId });
  qc.clear();
  window.location.assign("/");
}

/**
 * Toegang nemen tot een klant: vraagt bevestiging, kent het ondersteuningslidmaatschap toe en opent de klant.
 * Geef `request(client)` door aan knoppen; render `dialog` één keer.
 */
export function useAccessRequest() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [client, setClient] = useState<{ id: string; name: string } | null>(null);
  const [pending, setPending] = useState(false);

  const confirm = async () => {
    if (!client) return;
    setPending(true);
    try {
      await api.post(`/v1/platform/tenants/${client.id}/access`, {});
      await switchToTenant(client.id, qc);
    } catch (error) {
      setPending(false);
      toast({ tone: "danger", title: t("access.failed"), description: errorText(t, error) });
    }
  };

  const dialog = (
    <ConfirmDialog
      isOpen={client !== null}
      onOpenChange={(open) => (open || pending ? undefined : setClient(null))}
      title={t("access.title", { name: client?.name ?? "" })}
      body={t("access.body")}
      confirmLabel={t("access.confirm")}
      cancelLabel={t("common.cancel")}
      closeLabel={t("common.close")}
      isPending={pending}
      onConfirm={() => void confirm()}
    />
  );
  return { request: (c: { id: string; name: string }) => setClient(c), dialog };
}
